import React, { useState } from 'react';
import Navbar from '@/components/Navbar';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { KeyRound, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function AdminSettings() {
    const { admin, logout } = useAuth();
    const navigate = useNavigate();
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirmPw, setConfirmPw] = useState('');
    const [busy, setBusy] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (next.length < 8) { toast.error('New password must be at least 8 characters'); return; }
        if (next !== confirmPw) { toast.error('Passwords do not match'); return; }
        setBusy(true);
        try {
            await api.post('/auth/change-password', {
                current_password: current, new_password: next,
            });
            toast.success('Password updated — please sign in again');
            setCurrent(''); setNext(''); setConfirmPw('');
            // Force re-authentication: clear token + auth state, then navigate to login
            await logout();
            navigate('/admin/login', { replace: true });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-black">
            <Navbar variant="admin" />

            <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16">
                <div className="text-xs uppercase tracking-[0.28em] text-un-water font-semibold">Account</div>
                <h1 className="mt-2 font-display font-black uppercase tracking-tight text-white text-3xl md:text-4xl">
                    Settings
                </h1>
                <p className="mt-2 text-white/60 font-body">
                    Signed in as <span className="text-white font-semibold">{admin?.username}</span>
                </p>

                <div className="mt-10 rounded-3xl border border-white/10 bg-neutral-950 p-6 md:p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-un-lime text-black">
                            <KeyRound size={16} />
                        </span>
                        <div>
                            <div className="text-white font-semibold">Change password</div>
                            <div className="text-xs text-white/60">Choose a strong password (min. 8 characters).</div>
                        </div>
                    </div>

                    <form onSubmit={onSubmit} data-testid="change-password-form" className="grid grid-cols-1 gap-4 max-w-md">
                        <PwField label="Current password" testid="settings-current-password" value={current} onChange={setCurrent} />
                        <PwField label="New password" testid="settings-new-password" value={next} onChange={setNext} />
                        <PwField label="Confirm new password" testid="settings-confirm-password" value={confirmPw} onChange={setConfirmPw} />

                        <button
                            data-testid="settings-save-btn"
                            type="submit"
                            disabled={busy}
                            className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-un-lime text-black font-semibold px-5 py-3 hover:bg-white transition-colors disabled:opacity-60"
                        >
                            {busy ? <Loader2 className="animate-spin" size={16} /> : null}
                            {busy ? 'Updating…' : 'Update password'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

function PwField({ label, testid, value, onChange }) {
    return (
        <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.22em] text-white/70 mb-2">{label}</span>
            <input
                data-testid={testid}
                type="password"
                required
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full rounded-full bg-black border border-white/15 px-4 py-2.5 text-white placeholder:text-white/40 focus:outline-none focus:border-un-berry focus:ring-1 focus:ring-un-berry"
            />
        </label>
    );
}
